import { useMemo, useState, useEffect } from 'react';
import {
  createScaffolderFieldExtension,
  FieldExtensionComponentProps,
  CustomFieldExtensionSchema,
} from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  TextField,
  FormHelperText,
  Link,
} from '@material-ui/core';
import Autocomplete from '@mui/material/Autocomplete';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { useNobl9Metadata } from '@internal/plugin-nobl9-metadata-frontend';
import { renderLoadingAdornment } from './fieldUtils';

export const Nobl9DataSourceFieldSchema: CustomFieldExtensionSchema = {
  returnValue: { type: 'string' },
};

interface Props extends FieldExtensionComponentProps<string> {}

export const Nobl9DataSourceFieldComponent = ({
  onChange,
  formData,
  schema,
}: Props) => {
  const { dataSources, state, error } = useNobl9Metadata();
  const [value, setValue] = useState(formData || '');

  const selectedProjectName: string | undefined = (window as any)
    .__nobl9SelectedProjectName;
  const selectedProjectLinkageKey: string | undefined =
    (window as any).__nobl9SelectedProjectId || selectedProjectName;

  const filteredDataSources = useMemo(() => {
    const filtered = selectedProjectLinkageKey
      ? dataSources.filter(
          ds =>
            ds.projectId === selectedProjectLinkageKey ||
            ds.projectId === selectedProjectName,
        )
      : dataSources;
    // Map to include both name (for submission) and displayName (for display)
    return filtered.map(ds => ({
      name: ds.name,
      displayName: ds.displayName || ds.name,
    }));
  }, [dataSources, selectedProjectLinkageKey, selectedProjectName]);

  useEffect(() => {
    if (value) {
      const ok = /^[A-Za-z0-9_.-]+$/.test(value);
      if (ok) onChange(value);
    }
  }, [value, onChange]);

  const loading = state === 'loading' || state === 'refreshing';

  const rawDescription: string = (schema as any)?.description || '';
  const descriptionNodes = useMemo(() => {
    if (!rawDescription) return null;
    const parts: (string | JSX.Element)[] = [];
    const regex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
    let lastIndex = 0;
    for (const m of rawDescription.matchAll(regex)) {
      if (m.index! > lastIndex)
        parts.push(rawDescription.slice(lastIndex, m.index!));
      parts.push(
        <Link
          href={m[2]}
          target="_blank"
          rel="noopener noreferrer"
          underline="always"
          color="inherit"
        >
          {m[1]}
        </Link>,
      );
      lastIndex = (m.index || 0) + m[0].length;
    }
    if (lastIndex < rawDescription.length)
      parts.push(rawDescription.slice(lastIndex));
    return parts;
  }, [rawDescription]);
  const statusLine = useMemo(() => {
    if (loading) return 'Loading data sources…';
    if (error) return 'Error loading data sources';
    if (selectedProjectLinkageKey && !filteredDataSources.length)
      return 'No data sources for selected project';
    return '';
  }, [loading, error, selectedProjectLinkageKey, filteredDataSources.length]);
  return (
    <FormControl fullWidth margin="dense" error={!!error}>
      <Autocomplete
        disabled={loading}
        options={filteredDataSources}
        getOptionLabel={opt =>
          typeof opt === 'string' ? opt : opt.displayName
        }
        value={
          value
            ? filteredDataSources.find(ds => ds.name === value) || null
            : null
        }
        onChange={(_e, newOpt) => {
          setValue(newOpt ? newOpt.name : '');
        }}
        loading={loading}
        isOptionEqualToValue={(opt, val) =>
          typeof opt === 'object' && typeof val === 'object'
            ? opt.name === val.name
            : opt === val
        }
        renderInput={params => (
          <TextField
            {...params}
            label="Nobl9 Data Source"
            placeholder={
              selectedProjectName
                ? `Data Sources for ${selectedProjectName}`
                : 'Select a project first or type…'
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: renderLoadingAdornment(params, loading),
            }}
          />
        )}
        filterSelectedOptions
        autoHighlight
        clearOnBlur={false}
        openOnFocus
        noOptionsText={useMemo(() => {
          if (loading) return 'Loading…';
          if (selectedProjectLinkageKey)
            return 'No matching data sources for project';
          return 'Select a project first';
        }, [loading, selectedProjectLinkageKey])}
      />
      {(descriptionNodes || statusLine) && (
        <FormHelperText component="div" style={{ whiteSpace: 'pre-line' }}>
          {descriptionNodes}
          {descriptionNodes && statusLine ? '\n' : ''}
          {statusLine}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export const Nobl9DataSourceFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'Nobl9_DataSourceSelect',
    component: Nobl9DataSourceFieldComponent,
    schema: Nobl9DataSourceFieldSchema,
  }),
);

export default Nobl9DataSourceFieldExtension;
